import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';
import { mockTeamMembers, mockSettings, TeamMember } from '../../../mocks/step6Data';

type StatusFilter = 'tumu' | 'aktif' | 'pasif' | 'ilerliyor' | 'tamamladi';
type SortBy = 'puan' | 'isim' | 'son-iletisim' | 'adim';
type Tab = 'genel' | 'seviye' | 'performans';

const CURRENT_DATE = new Date('2026-03-25');

const statusColors: Record<string, string> = { aktif: 'bg-green-500', pasif: 'bg-red-500', ilerliyor: 'bg-yellow-500', tamamladi: 'bg-sky-500' };
const statusText: Record<string, string> = { aktif: 'text-green-400', pasif: 'text-red-400', ilerliyor: 'text-yellow-400', tamamladi: 'text-sky-400' };
const statusBg: Record<string, string> = { aktif: 'rgba(34,197,94,0.1)', pasif: 'rgba(239,68,68,0.1)', ilerliyor: 'rgba(234,179,8,0.1)', tamamladi: 'rgba(56,189,248,0.1)' };
const statusBarColor: Record<string, string> = { aktif: '#22C55E', ilerliyor: '#EAB308', tamamladi: '#38BDF8', pasif: '#EF4444' };
const statusLabels: Record<string, string> = { aktif: 'Aktif', pasif: 'Pasif', ilerliyor: 'İlerliyor', tamamladi: 'Tamamladı' };

function daysSince(dateStr: string) {
  return Math.floor((CURRENT_DATE.getTime() - new Date(dateStr).getTime()) / 86400000);
}

function perfScore(m: TeamMember): number {
  const statusPts: Record<string, number> = { aktif: 30, tamamladi: 25, ilerliyor: 15, pasif: 0 };
  const days = daysSince(m.lastContact);
  return (statusPts[m.status] ?? 0)
    + (m.step1Completed ? 20 : 0)
    + (m.ownTeam?.length || 0) * 10
    + (days <= 7 ? 20 : days <= 14 ? 10 : 0);
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: statusBg[status] }}>
      <span className={statusText[status]}>{statusLabels[status]}</span>
    </span>
  );
}

export default function EkipAnalizi() {
  const { profile } = useAuth();
  const [teamData, setTeamData] = useState<TeamMember[]>(mockTeamMembers);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (!profile) { setDataLoading(false); return; }

    let cancelled = false;
    supabase.from('step6_team_members').select('*').eq('user_id', profile.id).order('sort_order', { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (data && data.length > 0) {
          const l1Members = data.filter((m: Record<string, unknown>) => !m.parent_id);
          const l2Members = data.filter((m: Record<string, unknown>) => m.parent_id);
          const mapped: TeamMember[] = l1Members.map((l1: Record<string, unknown>) => ({
            id: l1.id as string,
            name: l1.name as string,
            phone: l1.phone as string,
            status: l1.status as TeamMember['status'],
            step1Completed: l1.step1_completed as boolean,
            lastContact: l1.last_contact as string,
            note: l1.note as string,
            joinDate: l1.join_date as string,
            ownTeam: l2Members.filter((l2: Record<string, unknown>) => l2.parent_id === l1.id).map((l2: Record<string, unknown>) => ({
              id: l2.id as string,
              name: l2.name as string,
              phone: l2.phone as string,
              status: l2.status as TeamMember['status'],
              step1Completed: l2.step1_completed as boolean,
              lastContact: l2.last_contact as string,
              note: l2.note as string,
              joinDate: l2.join_date as string,
            })),
          }));
          setTeamData(mapped);
        }
        setDataLoading(false);
      });

    return () => { cancelled = true; };
  }, [profile]);

  const [tab, setTab] = useState<Tab>('genel');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('tumu');
  const [sortBy, setSortBy] = useState<SortBy>('puan');
  const [expandedMember, setExpandedMember] = useState<string | null>(null);

  const l1 = teamData;
  const l2 = teamData.flatMap(m => m.ownTeam || []);
  const allMembers = [...l1, ...l2];

  const statusCounts = {
    aktif: allMembers.filter(m => m.status === 'aktif').length,
    pasif: allMembers.filter(m => m.status === 'pasif').length,
    ilerliyor: allMembers.filter(m => m.status === 'ilerliyor').length,
    tamamladi: allMembers.filter(m => m.status === 'tamamladi').length,
  };

  const activeRate = Math.round(((statusCounts.aktif + statusCounts.tamamladi) / allMembers.length) * 100);
  const step1Count = allMembers.filter(m => m.step1Completed).length;
  const incomePotential = l1.length * mockSettings.seviye1Kazanc + l2.length * mockSettings.seviye2Kazanc;
  const maxPotential = 4 * mockSettings.seviye1Kazanc + 16 * mockSettings.seviye2Kazanc + 64 * mockSettings.seviye3Kazanc;
  const contactSorted = [...allMembers].sort((a, b) => daysSince(b.lastContact) - daysSince(a.lastContact));

  const filteredMembers = useMemo(() => {
    let list = [...l1];
    if (statusFilter !== 'tumu') list = list.filter(m => m.status === statusFilter);
    if (sortBy === 'puan') list.sort((a, b) => perfScore(b) - perfScore(a));
    else if (sortBy === 'isim') list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === 'son-iletisim') list.sort((a, b) => new Date(b.lastContact).getTime() - new Date(a.lastContact).getTime());
    else if (sortBy === 'adim') list.sort((a, b) => (b.step1Completed ? 1 : 0) - (a.step1Completed ? 1 : 0));
    return list;
  }, [statusFilter, sortBy]);

  const tabs: [Tab, string, string][] = [
    ['genel', 'ri-pie-chart-2-line', 'Genel Bakış'],
    ['seviye', 'ri-sitemap-line', 'Seviye Analizi'],
    ['performans', 'ri-bar-chart-line', 'Performans'],
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Ekip Analizi</h1>
        <p className="text-sm text-gray-500 mt-1">Tüm ekibinin derinlemesine görünümü</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {tabs.map(([t, icon, label]) => (
          <button key={t} onClick={() => setTab(t)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap"
            style={tab === t
              ? { background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)', color: 'white' }
              : { color: '#6B7280' }}>
            <i className={`${icon} text-sm`} />
            {label}
          </button>
        ))}
      </div>

      {/* ═══════════════ GENEL BAKIŞ ═══════════════ */}
      {tab === 'genel' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Toplam Ekip', value: allMembers.length, icon: 'ri-group-line', color: 'text-white', bg: 'rgba(255,255,255,0.08)' },
              { label: 'Aktiflik Oranı', value: `%${activeRate}`, icon: 'ri-shield-check-line', color: 'text-green-400', bg: 'rgba(34,197,94,0.08)' },
              { label: '1. Adım Tamamlayan', value: step1Count, icon: 'ri-footprint-line', color: 'text-sky-400', bg: 'rgba(56,189,248,0.08)' },
              { label: 'Aylık Potansiyel', value: `₺${incomePotential.toLocaleString('tr-TR')}`, icon: 'ri-money-dollar-circle-line', color: 'text-amber-400', bg: 'rgba(245,166,35,0.08)' },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl p-5" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: s.bg }}>
                  <i className={`${s.icon} text-base ${s.color}`} />
                </div>
                <div className={`text-2xl font-black ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-400 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Durum Dağılımı */}
          <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,166,35,0.12)' }}>
                <i className="ri-pie-chart-2-line text-amber-400 text-base" />
              </div>
              <h2 className="text-sm font-bold text-white">Durum Dağılımı</h2>
            </div>
            <div className="space-y-4">
              {(['aktif', 'ilerliyor', 'tamamladi', 'pasif'] as const).map(status => {
                const count = statusCounts[status];
                const pct = Math.round((count / allMembers.length) * 100);
                return (
                  <div key={status}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`w-2.5 h-2.5 rounded-full ${statusColors[status]}`} />
                        <span className="text-xs font-medium text-gray-300">{statusLabels[status]}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">{count} kişi</span>
                        <span className={`text-xs font-black w-8 text-right ${statusText[status]}`}>%{pct}</span>
                      </div>
                    </div>
                    <div className="h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, background: statusBarColor[status] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Gelir Potansiyeli */}
          <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.12)' }}>
                <i className="ri-line-chart-line text-green-400 text-base" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-white">Gelir Potansiyeli</h2>
                <p className="text-xs text-gray-500">Mevcut ekiple kümülatif kazanç senaryosu</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              {[
                { label: 'Mevcut Ekip Geliri', value: `₺${incomePotential.toLocaleString('tr-TR')}`, sub: `${l1.length} L1 + ${l2.length} L2 kişi`, color: 'text-amber-400', bg: 'rgba(245,166,35,0.08)' },
                { label: 'Tam Kapasite (3. Adım)', value: `₺${maxPotential.toLocaleString('tr-TR')}`, sub: '4 + 16 + 64 kişilik ağ', color: 'text-purple-400', bg: 'rgba(168,85,247,0.08)' },
                { label: 'Kalan Potansiyel', value: `₺${(maxPotential - incomePotential).toLocaleString('tr-TR')}`, sub: 'Büyüme hedefi', color: 'text-emerald-400', bg: 'rgba(52,211,153,0.08)' },
              ].map((item, i) => (
                <div key={i} className="rounded-xl p-4" style={{ background: item.bg, border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div className={`text-lg font-black ${item.color}`}>{item.value}</div>
                  <div className="text-xs font-semibold text-white mt-1">{item.label}</div>
                  <div className="text-xs text-gray-600 mt-0.5">{item.sub}</div>
                </div>
              ))}
            </div>
            <div className="h-2.5 rounded-full overflow-hidden mb-2" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <div className="h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.min(100, Math.round((incomePotential / maxPotential) * 100))}%`, background: 'linear-gradient(90deg, #F5A623 0%, #22C55E 100%)' }} />
            </div>
            <p className="text-xs text-gray-600">Mevcut ekibin potansiyelinin <strong className="text-amber-400">%{Math.min(100, Math.round((incomePotential / maxPotential) * 100))}</strong>&apos;i kullanılıyor</p>
          </div>

          {/* Son İletişim Analizi */}
          <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.12)' }}>
                <i className="ri-time-line text-red-400 text-base" />
              </div>
              <h2 className="text-sm font-bold text-white">Son İletişim Analizi</h2>
              <span className="text-xs text-gray-600 ml-auto">En uzun bekleyenler önce</span>
            </div>
            <div className="space-y-2">
              {contactSorted.map(member => {
                const days = daysSince(member.lastContact);
                const urgency = days > 14 ? { text: 'text-red-400', bg: 'rgba(239,68,68,0.07)', label: 'Acil' } : days > 7 ? { text: 'text-yellow-400', bg: 'rgba(234,179,8,0.07)', label: 'Takip Et' } : { text: 'text-green-400', bg: 'rgba(34,197,94,0.07)', label: 'İyi' };
                return (
                  <div key={member.id} className="flex items-center gap-3 p-3 rounded-xl"
                    style={{ background: urgency.bg, border: '1px solid rgba(255,255,255,0.04)' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #6B7280 0%, #374151 100%)' }}>
                      {member.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-white">{member.name}</div>
                      <div className="text-xs text-gray-500">{member.lastContact}</div>
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-lg ${urgency.text}`} style={{ background: urgency.bg }}>
                      {days} gün
                    </span>
                    <span className={`text-xs font-bold w-16 text-right ${urgency.text}`}>{urgency.label}</span>
                    <a href={`https://wa.me/${member.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                      className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer flex-shrink-0"
                      style={{ background: 'rgba(37,211,102,0.15)' }}>
                      <i className="ri-whatsapp-line text-green-400 text-xs" />
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ═══════════════ SEVİYE ANALİZİ ═══════════════ */}
      {tab === 'seviye' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 1. Seviye */}
            <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,166,35,0.12)' }}>
                  <i className="ri-user-star-line text-amber-400 text-base" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-white">1. Seviye — İlk 4 Kişin</h2>
                  <p className="text-xs text-gray-500">{l1.length}/4 kişi</p>
                </div>
              </div>
              <div className="h-2 rounded-full overflow-hidden mb-4" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div className="h-full rounded-full" style={{ width: `${(l1.length / 4) * 100}%`, background: 'linear-gradient(90deg, #F5A623, #FF6B35)' }} />
              </div>
              <div className="space-y-2">
                {l1.map(member => (
                  <div key={member.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' }}>
                      {member.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-white">{member.name}</div>
                      <div className="text-xs text-gray-600">{(member.ownTeam || []).length} kişilik alt ekip</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={member.status} />
                      {member.step1Completed && <i className="ri-checkbox-circle-fill text-sky-400 text-xs" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 2. Seviye */}
            <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(56,189,248,0.12)' }}>
                  <i className="ri-group-line text-sky-400 text-base" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-white">2. Seviye — Ekibinin Ekibi</h2>
                  <p className="text-xs text-gray-500">{l2.length}/16 kişi potansiyel</p>
                </div>
              </div>
              <div className="h-2 rounded-full overflow-hidden mb-4" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div className="h-full rounded-full" style={{ width: `${Math.min(100, (l2.length / 16) * 100)}%`, background: 'linear-gradient(90deg, #38BDF8, #818CF8)' }} />
              </div>
              <div className="space-y-2">
                {l2.map(member => (
                  <div key={member.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #38BDF8 0%, #818CF8 100%)' }}>
                      {member.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-white">{member.name}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={member.status} />
                      {member.step1Completed && <i className="ri-checkbox-circle-fill text-sky-400 text-xs" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Ekip Ağacı */}
          <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(168,85,247,0.12)' }}>
                <i className="ri-sitemap-line text-purple-400 text-base" />
              </div>
              <h2 className="text-sm font-bold text-white">Ekip Ağacı</h2>
              <span className="text-xs text-gray-600 ml-auto">Karta tıkla, alt eklyi gör</span>
            </div>
            <div className="space-y-3">
              {l1.map(member => (
                <div key={member.id}>
                  <button
                    onClick={() => setExpandedMember(expandedMember === member.id ? null : member.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all hover:bg-white/5 text-left"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' }}>
                      {member.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white">{member.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{(member.ownTeam || []).length} kişilik alt ekip</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={member.status} />
                      <i className={`ri-arrow-${expandedMember === member.id ? 'up' : 'down'}-s-line text-gray-500`} />
                    </div>
                  </button>

                  {expandedMember === member.id && (
                    <div className="ml-8 mt-2 space-y-1.5 pl-4" style={{ borderLeft: '2px solid rgba(245,166,35,0.2)' }}>
                      {(member.ownTeam || []).length > 0 ? (member.ownTeam || []).map(tm => (
                        <div key={tm.id} className="flex items-center gap-3 p-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, #38BDF8 0%, #818CF8 100%)' }}>
                            {tm.name.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-white truncate">{tm.name}</div>
                            <div className="text-xs text-gray-600">{tm.phone}</div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${statusColors[tm.status]}`} />
                            {tm.step1Completed && <i className="ri-checkbox-circle-fill text-sky-400 text-xs" />}
                          </div>
                        </div>
                      )) : (
                        <p className="text-xs text-gray-600 italic py-2 pl-1">Henüz alt ekip üyesi yok</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ═══════════════ PERFORMANS ═══════════════ */}
      {tab === 'performans' && (
        <>
          {/* Filtrele & Sırala */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
              {(['tumu', 'aktif', 'ilerliyor', 'tamamladi', 'pasif'] as const).map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
                  style={statusFilter === s ? { background: 'rgba(245,166,35,0.2)', color: '#F5A623' } : { color: '#6B7280' }}>
                  {s === 'tumu' ? 'Tümü' : statusLabels[s]}
                </button>
              ))}
            </div>
            <select value={sortBy} onChange={e => setSortBy(e.target.value as SortBy)}
              className="px-3 py-2 rounded-xl text-xs font-medium text-gray-300 outline-none cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <option value="puan">Puana Göre Sırala</option>
              <option value="isim">İsme Göre Sırala</option>
              <option value="son-iletisim">Son İletişime Göre</option>
              <option value="adim">Adım Durumuna Göre</option>
            </select>
          </div>

          {/* Puan Sistemi Açıklaması */}
          <div className="rounded-2xl p-4 flex flex-wrap gap-4" style={{ background: 'rgba(245,166,35,0.04)', border: '1px solid rgba(245,166,35,0.1)' }}>
            <span className="text-xs text-gray-500 font-medium">Puan sistemi:</span>
            {[
              { label: 'Aktif durum', pts: '+30' },
              { label: '1. Adım tamamlandı', pts: '+20' },
              { label: 'Son 7 gün iletişim', pts: '+20' },
              { label: 'Alt ekip üyesi (kişi başı)', pts: '+10' },
            ].map((item, i) => (
              <span key={i} className="text-xs text-gray-400">
                <strong className="text-amber-400">{item.pts}</strong> {item.label}
              </span>
            ))}
          </div>

          {/* Performans Listesi */}
          <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,166,35,0.12)' }}>
                <i className="ri-bar-chart-line text-amber-400 text-base" />
              </div>
              <h2 className="text-sm font-bold text-white">Performans Sıralaması</h2>
              <span className="text-xs text-gray-600 ml-auto">{filteredMembers.length} kişi listeleniyor</span>
            </div>

            <div className="space-y-3">
              {filteredMembers.map((member, idx) => {
                const score = perfScore(member);
                const days = daysSince(member.lastContact);
                const scoreColor = score >= 60 ? '#22C55E' : score >= 35 ? '#F5A623' : '#EF4444';
                return (
                  <div key={member.id} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-bold text-gray-600 w-5 flex-shrink-0">#{idx + 1}</span>
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' }}>
                        {member.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white">{member.name}</div>
                        <div className="text-xs text-gray-500">{days} gün önce iletişim &nbsp;·&nbsp; {(member.ownTeam || []).length} alt ekip üyesi</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xl font-black" style={{ color: scoreColor }}>{score}</div>
                        <div className="text-xs text-gray-600">puan</div>
                      </div>
                    </div>

                    <div className="h-1.5 rounded-full overflow-hidden mb-3" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(100, (score / 100) * 100)}%`, background: scoreColor }} />
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      <StatusBadge status={member.status} />
                      {member.step1Completed
                        ? <span className="text-xs px-2 py-0.5 rounded-full text-sky-400" style={{ background: 'rgba(56,189,248,0.1)' }}>1. Adım ✓</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full text-red-400" style={{ background: 'rgba(239,68,68,0.08)' }}>1. Adım Eksik</span>}
                      {days > 14 && <span className="text-xs px-2 py-0.5 rounded-full text-red-400" style={{ background: 'rgba(239,68,68,0.08)' }}>Uzun süredir haberleşilmedi</span>}
                      {score >= 70 && <span className="text-xs px-2 py-0.5 rounded-full text-emerald-400" style={{ background: 'rgba(52,211,153,0.1)' }}>Yıldız Üye</span>}
                    </div>
                  </div>
                );
              })}

              {filteredMembers.length === 0 && (
                <p className="text-sm text-gray-600 text-center py-8">Bu filtreye uygun kişi bulunamadı</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
